import axios from 'axios';
import { Asset, AssetHistoryEntry } from '../types/Asset';

const BASE_URL = process.env.REACT_APP_API_URL;

export interface CreateAssetData {
  name: string;
  description?: string;
  assetType: string;
  assetNumber: string;
  contractAreaId: number;
  startLatitude?: number;
  startLongitude?: number;
  endLatitude?: number;
  endLongitude?: number;
  highwayUnique?: string;
  parentAsset?: string;
  numberedRoad?: boolean;
  summerClass?: string;
  winterClass?: string;
  length?: number;
  status?: string;
}

export interface PaginatedAssetsResponse {
  data: Asset[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface AssetQueryParams {
  page?: number;
  limit?: number;
  serviceAreaId?: number;
  contractAreaId?: number;
  assetType?: string;
  search?: string;
  /** Server-side sort (allowlisted): name, assetNumber, assetType, status, subtype, description, length, winterClass, summerClass, contractArea. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface AssetFilterOptions {
  serviceAreas: Array<{ id: number; name: string; code: string }>;
  contractAreas: Array<{ id: number; name: string; code?: string }>;
  assetTypes: string[];
}

class AssetService {
  async getAllAssets(): Promise<Asset[]> {
    try {
      const response = await axios.get(`${BASE_URL}/assets`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAssetsPaginated(params: AssetQueryParams = {}): Promise<PaginatedAssetsResponse> {
    const queryParams = new URLSearchParams();
    
    // Add pagination parameters
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    
    // Add filter parameters
    if (params.serviceAreaId) queryParams.append('serviceAreaId', params.serviceAreaId.toString());
    if (params.contractAreaId) queryParams.append('contractAreaId', params.contractAreaId.toString());
    if (params.assetType) queryParams.append('assetType', params.assetType);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);

    const url = `${BASE_URL}/assets?${queryParams.toString()}`;
    
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getFilterOptions(): Promise<AssetFilterOptions> {
    try {
      const response = await axios.get(`${BASE_URL}/assets/filter-options`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAssetById(id: number): Promise<Asset> {
    try {
      const response = await axios.get(`${BASE_URL}/assets/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAssetHistory(id: number): Promise<AssetHistoryEntry[]> {
    try {
      const response = await axios.get(`${BASE_URL}/assets/${id}/history`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async createAsset(assetData: CreateAssetData): Promise<Asset> {
    try {
      const response = await axios.post(`${BASE_URL}/assets`, assetData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async updateAsset(id: number, assetData: Partial<CreateAssetData>): Promise<Asset> {
    try {
      const response = await axios.put(`${BASE_URL}/assets/${id}`, assetData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async deleteAsset(id: number): Promise<void> {
    try {
      await axios.delete(`${BASE_URL}/assets/${id}`);
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async toggleAssetStatus(id: number): Promise<Asset> {
    try {
      const response = await axios.patch(`${BASE_URL}/assets/${id}/toggle-status`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async getAssetsByForemanArea(foremanAreaId: number, assetType?: string): Promise<Asset[]> {
    try {
      return this.searchAssets({ foremanAreaId, assetType });
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async searchAssets(params: { foremanAreaId?: number; contractAreaId?: number; assetType?: string; assetNumber?: string; serviceAreaId?: number }): Promise<Asset[]> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (params.foremanAreaId) {
        queryParams.append('foremanAreaId', params.foremanAreaId.toString());
      }
      
      if (params.contractAreaId) {
        queryParams.append('contractAreaId', params.contractAreaId.toString());
      }
      
      if (params.assetType) {
        queryParams.append('assetType', params.assetType);
      }

      if (params.assetNumber) {
        queryParams.append('assetNumber', params.assetNumber);
      }

      if (params.serviceAreaId) {
        queryParams.append('serviceAreaId', params.serviceAreaId.toString());
      }
      
      const response = await axios.get(`${BASE_URL}/assets/search?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Lightweight search method for asset selection dropdowns (returns minimal fields)
  async searchAssetsLight(params: {
    contractAreaId?: number;
    foremanAreaId?: number;
    assetType?: string;
    assetNumber?: string;
    serviceAreaId?: number;
  }): Promise<Asset[]> {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      
      if (params.contractAreaId) {
        queryParams.append('contractAreaId', params.contractAreaId.toString());
      }

      if (params.foremanAreaId) {
        queryParams.append('foremanAreaId', params.foremanAreaId.toString());
      }
      
      if (params.assetType) {
        queryParams.append('assetType', params.assetType);
      }

      if (params.assetNumber) {
        queryParams.append('assetNumber', params.assetNumber);
      }

      if (params.serviceAreaId) {
        queryParams.append('serviceAreaId', params.serviceAreaId.toString());
      }
      
      const response = await axios.get(`${BASE_URL}/assets/search-light?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  private handleError(error: any): Error {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.error || error.message;
      return new Error(message);
    }
    return error instanceof Error ? error : new Error('An unknown error occurred');
  }
}

export const assetService = new AssetService();
export default assetService; 